// ============================================================================
// vpn/VpnAccessBlock.tsx — доступ к профилю по РОЛЯМ (пусто = всем).
// SSoT сервера: SetVpnAccess — полная замена; живое обновление — шина
// vpn.access.updated (перечитывает роли без перезагрузки).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { createLogger } from '@/lib/logger';
import { eventBusHub } from '@/lib/eventBusHub';

const log = createLogger('VpnAccessBlock');

export interface VpnRoleItem {
  id: string;
  name: string;
}

interface VpnAccessBlockProps {
  profileCode: string;
  roles: VpnRoleItem[];
  busy: boolean;
}

export function VpnAccessBlock({ profileCode, roles, busy }: VpnAccessBlockProps) {
  const [accessRoles, setAccessRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const loadAccess = useCallback(async () => {
    if (!profileCode) { setAccessRoles([]); return; }
    try {
      const r = await getSDK().vpn.getVpnAccess({ profileCode });
      setAccessRoles(r.roleIds || []);
    } catch (e) {
      log.error('load vpn access', { error: e, code: profileCode });
      setAccessRoles([]);
    }
  }, [profileCode]);

  useEffect(() => { void loadAccess(); }, [loadAccess]);

  // Живое обновление: набор ролей доступа изменён (шина).
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      if (ev.topic === 'vpn.access.updated' && String(ev.payload?.profile_code || '') === profileCode) {
        void loadAccess();
      }
    });
    return unsub;
  }, [profileCode, loadAccess]);

  const save = async () => {
    if (!profileCode) {
      useToastStore.getState().showError('Сначала сохраните профиль');
      return;
    }
    setSaving(true);
    try {
      await getSDK().vpn.setVpnAccess({ profileCode, roleIds: accessRoles });
      useToastStore.getState().showSuccess(accessRoles.length ? 'Доступ по ролям сохранён' : 'Доступ открыт всем');
    } catch (e) {
      useToastStore.getState().showError(e instanceof Error ? e.message : 'Ошибка сохранения доступа');
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (id: string) =>
    setAccessRoles((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <div className="border-t pt-3 space-y-2">
      <div className="text-xs font-medium">Доступ по ролям (ничего не выбрано = всем)</div>
      <div className="flex flex-wrap gap-2">
        {roles.map((r) => (
          <label key={r.id} className="flex items-center gap-1.5 text-xs border rounded px-2 py-1">
            <input type="checkbox" checked={accessRoles.includes(r.id)} onChange={() => toggleRole(r.id)} />
            {r.name}
          </label>
        ))}
        {roles.length === 0 && <span className="text-xs text-muted-foreground">Роли не загружены</span>}
      </div>
      <Button variant="outline" size="sm" onClick={() => void save()} disabled={busy || saving || !profileCode}>
        Сохранить доступ
      </Button>
    </div>
  );
}
